import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ChildAirlinesService } from "../../../../../account/services/child-airlines/child-airlines.service";
import { Validators } from '../../../../../common/forms';
import { DateTimeHelper } from '../../../../../common/helpers/datetime/datetime.helper';
import { FormValidationErrorsHelper } from "../../../../../common/helpers/forms/form-validation-errors-helper";
import { US_INTEGER_NUMBER_MASK } from "../../../../../common/helpers/text-masks/us-integer-number.mask";
import {
    CurrencyCollection,
    FuelPreferenceCollection,
    UnitsCollection
} from '../../../../../common/models';
import { FieldConfig as BackendFieldConfig } from '../../../../../common/models/field-registry.model';
import { CompanyValidationRulesResponse } from '../../../../../common/models/validation-rules.model';
import { CurrencyService, FuelPreferenceService, UnitsService } from "../../../../../common/services";
import { FieldRegistryService } from '../../../../../common/services/field-registry/field-registry.service';
import { FlightTypeService } from "../../../../../common/services/flight-type/flight-type.service";
import { ValidationRulesService } from '../../../../../common/services/validation-rules/validation-rules.service';
import { ToastService } from '../../../../../main/services';
import { createContractForm } from '../../../../forms';
import { Contract, Tender } from '../../../../models';
import { LocationTagsService, TenderContractsService } from '../../../../services';

export interface FieldConfig extends BackendFieldConfig {
    formControlName: string;
    type: string;
    colClass?: string;
    suffixes?: string;
    maxlength?: number;
    rows?: number;
    popoverTitle?: string;
    popoverContent?: string;
    placeholder?: string;
}

@Component({
    selector: 'jft-contract-form-modal',
    templateUrl: './contract-form-modal.component.html',
    styleUrls: ['./contract-form-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContractFormModalComponent implements OnInit {

    public form: UntypedFormGroup;
    public disableLocation: boolean = false;
    public US_INTEGER_NUMBER_MASK = US_INTEGER_NUMBER_MASK;
    public childCompanies = [];
    public flightTypes = [];
    public isFormReady: boolean = false;
    public locationTags: any[] = [];  // Available location tags
    public selectedLocationTags: any[] = [];  // Selected location tags
    public validationRules: CompanyValidationRulesResponse;

    public isRequiredCompany: boolean = false;
    public isRequiredAwardQuota: boolean = false;
    public isRequiredCurrency: boolean = false;
    public isRequiredUnits: boolean = false;
    public isRequiredContractStartDate: boolean = false;
    public isRequiredContractEndDate: boolean = false;
    public isRequiredFuelPreference: boolean = false;
    public isRequiredAverageUplift: boolean = false;
    public isRequiredMonthsOfService: boolean = false;
    public isRequiredMonthlyVolume: boolean = false;
    public isRequiredFuelType: boolean = false;
    public isRequiredPreferredIataServiceLevel: boolean = false;
    public isRequiredAnnualFrequency: boolean = false;
    public isRequiredFlightType: boolean = false;
    public isRequiredNotes: boolean = false;
    public isHiddenUnits: boolean = false;

    public fields: FieldConfig[] = [];

    constructor(
        public contract: Contract,
        public tender: Tender,
        public currencyCollection: CurrencyCollection,
        public unitsCollection: UnitsCollection,
        public fuelPreferenceCollection: FuelPreferenceCollection,
        private toast: ToastService,
        private service: TenderContractsService,
        private activeModal: NgbActiveModal,

        private currencyService: CurrencyService,
        private unitsService: UnitsService,
        private fuelPreferenceService: FuelPreferenceService,
        private childAirlinesService: ChildAirlinesService,
        private flightTypeService: FlightTypeService,
        private locationTagsService: LocationTagsService,
        private validationRulesService: ValidationRulesService,
        private fieldRegistryService: FieldRegistryService
    ) {
    }

    public ngOnInit() {
        this.getCurrencies();
        this.getUnits();
        this.getFuelPreference();
        this.getLocationTags();

        // Load necessary data first, then initialize form
        forkJoin([
            this.childAirlinesService.fetch(),
            this.flightTypeService.fetch(),
            this.validationRulesService.getRules()
        ]).subscribe(([childAirlines, flightTypes, validationRules]) => {
            this.childCompanies = childAirlines;
            this.flightTypes = flightTypes;
            this.validationRules = validationRules;
            const rules = validationRules;

            // Helper for local flags
            const isRequired = (key) => rules[key]?.is_required ?? this.tender.company.settings.isRequiredField(key);
            const isHidden = (key) => rules[key]?.is_hidden ?? this.tender.company.settings.isHiddenField(key);

            // Initialize required field flags based on validation rules
            this.isRequiredCompany = isRequired('location_company');
            this.isRequiredAwardQuota = isRequired('location_award_quota');
            this.isRequiredCurrency = isRequired('location_preferred_currency') || isRequired('location_currency');
            this.isRequiredUnits = isRequired('location_preferred_units') || isRequired('location_units');
            this.isRequiredContractStartDate = isRequired('location_contract_start_date');
            this.isRequiredContractEndDate = isRequired('location_contract_end_date');
            this.isRequiredFuelPreference = isRequired('location_delivery_point');
            this.isRequiredAverageUplift = isRequired('location_average_uplift');
            this.isRequiredMonthsOfService = isRequired('location_months_of_service');
            this.isRequiredMonthlyVolume = isRequired('location_monthly_volume');
            this.isRequiredFuelType = isRequired('location_fuel_type');
            this.isRequiredPreferredIataServiceLevel = isRequired('location_iata_service_level');
            this.isRequiredAnnualFrequency = isRequired('location_annual_frequency');
            this.isRequiredFlightType = isRequired('location_flight_type');
            this.isRequiredNotes = isRequired('location_notes');
            this.isHiddenUnits = isHidden('location_preferred_units') || isHidden('location_units');

            // Fetch dynamic field configuration using tender's company as SSOT
            this.fieldRegistryService.getFieldConfigForTender(this.tender.id, 'location_import').subscribe(backendFields => {
                // Now create the form with knowledge of child companies and validation rules
                this.form = createContractForm(this.contract, this.tender, this.childCompanies, rules);

                // Initialize fields configuration using backend data
                this.initFields(backendFields);

                // Update form validators after data is loaded
                this.updateCompanyValidators();

                // Initialize form values
                this.initializeCompanyFields();

                // Set up form subscriptions
                this.setupFormSubscriptions();

                // Mark form as ready
                this.isFormReady = true;
            });
        });
    }

    private initFields(backendFields: BackendFieldConfig[]) {
        // Map backend keys to frontend form control names
        const keyMap: {[key: string]: { control: string, type: string, col?: string, suffixes?: string }} = {
            'location_iata': { control: 'location_id', type: 'location_search', col: 'col-12 col-md-4' },
            'location_company': { control: 'company_info', type: 'company', col: 'col-12 col-md-4' },
            'location_award_quota': { control: 'award_quota', type: 'award_quota', col: 'col-12 col-md-4' },
            'location_preferred_currency': { control: 'currency_id', type: 'currnecy', col: 'col-12 col-md-4' },
            'location_preferred_units': { control: 'units_id', type: 'units', col: 'col-12 col-md-4' },
            'location_delivery_point': { control: 'fuel_preference_id', type: 'delivery_point', col: 'col-12 col-md-4' },
            'location_contract_start_date': { control: 'date_start', type: 'date', col: 'col-12 col-md-4' },
            'location_contract_end_date': { control: 'date_end', type: 'date', col: 'col-12 col-md-4' },
            'location_average_uplift': { control: 'average_uplift', type: 'average_uplift', col: 'col-12 col-md-4' },
            'location_months_of_service': { control: 'month_service', type: 'text', col: 'col-12 col-md-4' },
            'location_contract_volume': { control: 'volume', type: 'number', col: 'col-12 col-md-4' },
            'location_monthly_volume': { control: 'monthly_volume', type: 'number', col: 'col-12 col-md-4' },
            'location_fuel_type': { control: 'fuel_type_id', type: 'fuel_type', col: 'col-12 col-md-4' },
            'location_iata_service_level': { control: 'iata_service_level_id', type: 'service_level', col: 'col-12 col-md-4' },
            'location_annual_frequency': { control: 'annual_frequency', type: 'number', col: 'col-12 col-md-4', suffixes: 'Flights' },
            'location_flight_type': { control: 'flight_type_id', type: 'select', col: 'col-12 col-md-4' },
            'location_branded': { control: 'branded', type: 'checkbox', col: 'col-12 col-md-4' },
            'location_notes': { control: 'notes', type: 'textarea', col: 'col-12' },
            'location_tags': { control: 'location_tags', type: 'tags', col: 'col-12' }
        };

        this.fields = backendFields
            .filter(f => (!f.hidden || f.key === 'location_iata') && !!keyMap[f.key])
            .map(f => {
                const map = keyMap[f.key];

                let type = map.type;
                let formControlName = map.control;
                let label = f.label;

                // Special handling for Company field which can be 'company' or 'managed_company'
                if (f.key === 'location_company') {
                    const hasChildCompanies = this.childCompanies && this.childCompanies.length > 0;
                    if (hasChildCompanies) {
                        type = 'managed_company';
                        label = 'Managed Company';
                        formControlName = 'company_child_id';
                    }
                }

                return {
                    ...f,
                    formControlName: formControlName,
                    type: type,
                    label: label,
                    colClass: map.col || 'col-12 col-md-4',
                    suffixes: map.suffixes,
                    options: f.key === 'location_flight_type' ? this.flightTypes : f.options
                } as FieldConfig;
            });
    }

    private calculateFormVolumesRelation() {
        this.form.controls.monthly_volume.valueChanges.subscribe((value) => {
            let monthlyVolume = value //value.replace(/,/g, '.');
            let monthService = this.form.get('month_service').value;
            if (monthService && monthlyVolume && !isNaN(monthlyVolume) && !isNaN(monthService)) {
                this.form.controls.volume.patchValue((monthlyVolume * monthService).toFixed(2), { emitEvent: false, onlySelf: true });
            }
        });

        this.form.controls.volume.valueChanges.subscribe((value) => {
            let yearlyVolume = value // value.replace(/,/g, '.');
            let monthService = this.form.get('month_service').value;
            if (monthService && yearlyVolume && !isNaN(yearlyVolume) && !isNaN(monthService) && monthService > 0) {
                this.form.controls.monthly_volume.patchValue((yearlyVolume / monthService).toFixed(2), {
                    emitEvent: false,
                    onlySelf: true
                });
            }
        });

        this.form.controls.month_service.valueChanges.subscribe((value) => {
            let volume = this.form.get('volume').value;
            let monthlyVolume = this.form.get('monthly_volume').value;
            if (volume) {
                this.form.controls.monthly_volume.patchValue((volume / value).toFixed(2), {
                    emitEvent: false,
                    onlySelf: true
                });
            } else if (monthlyVolume) {
                this.form.controls.volume.patchValue((value * monthlyVolume).toFixed(2), {
                    emitEvent: false,
                    onlySelf: true
                });
            }
        });

        let volume = this.form.get('volume').value;
        let monthService = this.form.get('month_service').value;
        if (volume) {
            this.form.controls.monthly_volume.patchValue((volume / monthService).toFixed(2), {
                emitEvent: false,
                onlySelf: true
            });
        }

        // this.form.controls.average_uplift.valueChanges.subscribe((value) => {
        //     let average_uplift = this.form.get('average_uplift').value;
        //     if (average_uplift) {
        //         this.form.controls.average_uplift.patchValue(this.toIntValue(average_uplift), {
        //             emitEvent: false, onlySelf: true
        //         });
        //     }
        // });
    }

    public getCurrencies() {
        this.currencyService.fetch().subscribe(data => {
            this.currencyCollection = data;
        })
    }



    private setupFormSubscriptions() {
        if (!this.form) return;

        this.form.controls.award_quota.valueChanges.subscribe((value) => {
            value = parseInt(value);
            if (isNaN(value)) {
                value = 100; // Default to 100% instead of null
            } else {
                if (value < 1) {
                    value = 1;
                }
                if (value > 100) {
                    value = 100;
                }
            }
            this.form.controls.award_quota.patchValue(value, { emitEvent: false, onlySelf: true });
        });

        this.calculateFormVolumesRelation();
        this.applySpecialFlightTypeRelaxations();
    }

    private initializeCompanyFields() {
        if (!this.form) return;

        const hasChildCompanies = this.childCompanies && this.childCompanies.length > 0;

        if (hasChildCompanies) {
            // If company has child companies, use company_child_id
            if (this.contract.companyChild && this.contract.companyChild.id) {
                this.form.controls.company_child_id.patchValue(this.contract.companyChild.id, { emitEvent: false });
            }
            // Clear company_info when using child companies
            this.form.controls.company_info.patchValue('', { emitEvent: false });
        } else {
            // If no child companies, use company_info
            if (this.contract.companyInfo) {
                this.form.controls.company_info.patchValue(this.contract.companyInfo, { emitEvent: false });
            }
            // Clear company_child_id when not using child companies
            this.form.controls.company_child_id.patchValue('', { emitEvent: false });
        }
    }



    public getLocationTags() {
        // Fetch available location tags (separate from supplier tags)
        this.locationTagsService.fetchLocationTags().subscribe(data => {
            this.locationTags = data.map(tag => ({ id: tag.id, name: tag.name }));
            // Initialize selected tags from contract
            this.selectedLocationTags = this.contract.tags || [];
        })
    }

    public addLocationTag(name: string) {
        // Create new tag and return it
        return { id: '', name: name };
    }

    public locationTagChange(tags: any[]) {
        // Update form control with selected tags
        this.form.controls.location_tags.patchValue(tags, { emitEvent: false });
        this.form.markAsDirty();
    }

    public updateCompanyValidators() {
        if (!this.form) return;

        const isRequiredCompany = this.tender.company.settings.isRequiredField('location_company');
        const isHiddenCompany = this.tender.company.settings.isHiddenField('location_company');
        const hasChildCompanies = this.childCompanies && this.childCompanies.length > 0;

        // Clear existing validators
        this.form.controls.company_info.clearValidators();
        this.form.controls.company_child_id.clearValidators();

        // Apply correct validators based on company structure and settings
        if (!isHiddenCompany && isRequiredCompany) {
            if (hasChildCompanies) {
                // If company has child companies, validate company_child_id (dropdown)
                this.form.controls.company_child_id.setValidators([Validators.required]);
            } else {
                // If no child companies, validate company_info (text field)
                this.form.controls.company_info.setValidators([Validators.required]);
            }
        }

        // Update validity
        this.form.controls.company_info.updateValueAndValidity();
        this.form.controls.company_child_id.updateValueAndValidity();
    }

    public getUnits() {
        this.unitsService.fetch().subscribe(data => {
            this.unitsCollection = data;
        })
    }

    public getFuelPreference() {
        this.fuelPreferenceService.fetch().subscribe(data => {
            this.fuelPreferenceCollection = data;
        })
    }

    public isNewContract() {
        return !this.contract.id;
    }

    public submitContract() {
        if (this.isNewContract()) {
            this.saveContract();
        } else {
            this.updateContract();
        }
    }

    public saveContract() {
        this.form.disable({ emitEvent: false });
        this.convertDataBeforeSubmit();

        this.service.create(this.tender, this.contract, this.form.value)
            .pipe(finalize(() => this.form.enable()))
            .subscribe((contract: Contract) => {
                this.toast.success(`Location added`);
                this.activeModal.close(contract);
            }, (err) => {
                this.handleValidationErrors(err);
            });
    }

    public updateContract() {
        this.form.disable({ emitEvent: false });
        this.convertDataBeforeSubmit();
        this.service.update(this.tender, this.contract, this.form.value)
            .pipe(finalize(() => this.form.enable()))
            .subscribe((contract: Contract) => {
                this.toast.success(`Location updated`);
                this.activeModal.close(contract);
            }, (err) => {
                this.handleValidationErrors(err);
            });
    }

    private convertDataBeforeSubmit() {//todo refactor
        let annualFrequency = this.form.get('annual_frequency').value;
        if (annualFrequency == '') {
            this.form.controls.annual_frequency.patchValue(null, {
                emitEvent: false,
            });
        }

        // Convert empty string values to null for optional foreign key fields
        let flightTypeId = this.form.get('flight_type_id').value;
        if (flightTypeId === '' || flightTypeId === undefined) {
            this.form.controls.flight_type_id.patchValue(null, {
                emitEvent: false,
            });
        }

        let fuelTypeId = this.form.get('fuel_type_id').value;
        if (fuelTypeId === '' || fuelTypeId === undefined) {
            this.form.controls.fuel_type_id.patchValue(null, {
                emitEvent: false,
            });
        }

        let companyChildId = this.form.get('company_child_id').value;
        if (companyChildId === '' || companyChildId === undefined) {
            this.form.controls.company_child_id.patchValue(null, {
                emitEvent: false,
            });
        }

        // Ensure only one company field is used based on company structure
        const hasChildCompanies = this.childCompanies && this.childCompanies.length > 0;
        if (hasChildCompanies) {
            // If using child companies, clear company_info
            this.form.controls.company_info.patchValue('', { emitEvent: false });
        } else {
            // If not using child companies, clear company_child_id
            this.form.controls.company_child_id.patchValue(null, { emitEvent: false });
        }

        let yearlyVolume = this.form.get('volume').value;

        // Handle special flight types that allow zero volume
        const currentFlightTypeId = this.form.get('flight_type_id').value;
        const selectedFlightType = this.flightTypes.find(ft => ft.id === currentFlightTypeId);
        const specialFlightNames = ['schedule b', 'general aviation', 'seasonal', 'diversion'];
        const isSpecialFlightType = selectedFlightType && specialFlightNames.includes((selectedFlightType.name || '').toLowerCase());

        // If volume is empty or zero for special flight types, default to 1
        if (isSpecialFlightType && (!yearlyVolume || yearlyVolume === '' || yearlyVolume === '0' || yearlyVolume === 0)) {
            yearlyVolume = '1';
        }

        yearlyVolume = yearlyVolume.toString() //.replace(/,/g, '.');
        this.form.controls.volume.patchValue(yearlyVolume.replace(/,/g, ''), {
            emitEvent: false,
        });

        this.form.value.date_start = DateTimeHelper.convertToDateFormat(this.form.value.date_start);
        this.form.value.date_end = DateTimeHelper.convertToDateFormat(this.form.value.date_end);

        // Ensure award_quota is a valid integer
        let awardQuota = parseInt(this.form.value.award_quota);
        if (isNaN(awardQuota) || awardQuota < 1) {
            awardQuota = 100;
        }
        this.form.value.award_quota = awardQuota;

        if (!this.isNewContract()) {
            this.form.value.location = this.form.value.location ? this.form.value.location.id : '';
        }
    }

    public toIntValue(value: any) {
        return parseInt(value.toString().replace(/[^\d.-]/g, ''))
    }

    public getFormGroupErrorClasses(control: AbstractControl, isInvalid: boolean = false): {} {
        if (!control) {
            return {};
        }
        if (isInvalid) {
            return { 'is-invalid': control.invalid };
        }
        return { 'is-invalid': control.invalid && control.touched };
    }

    public showErrorBlock(control: AbstractControl): boolean {
        return control && control.errors && (control.dirty || control.touched);
    }

    public close() {
        this.activeModal.dismiss();
    }

    private handleValidationErrors(err: any) {
        console.error('Validation error:', err);

        if (err.error && typeof err.error === 'object') {
            // Handle field-specific validation errors
            const errors = err.error;
            let errorMessages = [];

            // Apply field-specific errors to form controls
            Object.keys(errors).forEach(fieldName => {
                const control = this.form.get(fieldName);
                if (control) {
                    // Set custom error on the form control
                    control.setErrors({ serverError: errors[fieldName] });
                    control.markAsTouched();
                }

                // Collect error messages for toast
                if (Array.isArray(errors[fieldName])) {
                    errorMessages.push(...errors[fieldName]);
                } else {
                    errorMessages.push(errors[fieldName]);
                }
            });

            // Show combined error message in toast
            if (errorMessages.length > 0) {
                this.toast.error(errorMessages.join(', '), 'Validation Error');
            } else {
                this.toast.error('Validation failed', 'Add Location');
            }
        } else {
            // Fallback for other error types
            this.toast.error(err.error || 'An error occurred', 'Add Location');
        }
    }

    public getFirstFormGroupErrorLabel(form: UntypedFormGroup) {
        if (form.valid) {
            return ''
        }

        let formValidationErrors = new FormValidationErrorsHelper();

        let formErrorLabel = formValidationErrors.getFirstErrorFormLabel(form);

        if (form.controls.volume.errors && form.controls.volume.errors.integer) {
            return 'Volume / Integer value format is required';
        }
        if (form.controls.location_id.errors && form.controls.location_id.errors.required) {
            return 'Location / Valid value is required';
        }
        // if (form.controls.average_uplift.errors) {
        //     return 'Average Uplift number format is invalid';
        // }

        if (formErrorLabel) {
            return formErrorLabel;
        }
        return '';
    }

    public onChangeServiceLevel(id: any) {
        this.form.controls.iata_service_level_id.patchValue(id && parseInt(id) ? id : null, { emitEvent: false })
    }

    public onChangeFuelType(id: any) {
        this.form.controls.fuel_type_id.patchValue(id && parseInt(id) ? parseInt(id) : null, { emitEvent: false })
    }

    public isVolumeRequired(): boolean {
        const flightTypeId = this.form?.get('flight_type_id')?.value;
        const selectedFlightType = this.flightTypes.find(ft => ft.id === flightTypeId);
        const specialFlightNames = ['schedule b', 'general aviation', 'seasonal', 'diversion'];
        const isSpecialFlightType = selectedFlightType && specialFlightNames.includes((selectedFlightType.name || '').toLowerCase());
        const isScheduleA = selectedFlightType && selectedFlightType.name.toLowerCase() === 'schedule a';

        // Schedule A always requires volume, special flight types don't, others follow company settings
        if (isScheduleA) {
            return true;
        } else if (isSpecialFlightType) {
            return false;
        } else {
            return this.tender.company.settings.isRequiredField('location_contract_volume');
        }
    }


    private applySpecialFlightTypeRelaxations() {
        if (!this.form) return;
        const relaxIfSpecialFlightType = () => {
            const flightTypeId = this.form.get('flight_type_id')?.value;
            // Special flight types that allow zero volume: Schedule B, General Aviation, Seasonal, Diversion
            // Schedule A requires volume
            const specialFlightNames = ['schedule b', 'general aviation', 'seasonal', 'diversion'];
            let isSpecialFlightType = false;

            // Find the selected flight type from the loaded flight types
            const selectedFlightType = this.flightTypes.find(ft => ft.id === flightTypeId);
            if (selectedFlightType) {
                isSpecialFlightType = specialFlightNames.includes((selectedFlightType.name || '').toLowerCase());
            }

            // Special flight type validation logic:
            // - Schedule A: volume is required (regardless of company settings)
            // - Schedule B, General Aviation, Seasonal, Diversion: volume is optional (allow zero)
            // - Other flight types: follow company settings
            const volCtrl = this.form.get('volume');
            if (volCtrl) {
                volCtrl.clearValidators();

                const isScheduleA = selectedFlightType && selectedFlightType.name.toLowerCase() === 'schedule a';

                if (isScheduleA) {
                    // Schedule A always requires volume
                    volCtrl.setValidators([Validators.required, Validators.min(0.01)]);
                } else if (isSpecialFlightType) {
                    // Allow zero volume for special flight types (no minimum validation)
                    volCtrl.setValidators([]);
                } else {
                    // For other flight types, follow company settings
                    const isRequiredVolume = this.tender.company.settings.isRequiredField('location_contract_volume');
                    if (isRequiredVolume) {
                        volCtrl.setValidators([Validators.required, Validators.min(0.01)]);
                    } else {
                        volCtrl.setValidators([Validators.min(0.01)]);
                    }
                }
                volCtrl.updateValueAndValidity({ emitEvent: false });
            }
        };
        // Run once and on flight_type change
        relaxIfSpecialFlightType();
        this.form.get('flight_type_id')?.valueChanges.subscribe(() => relaxIfSpecialFlightType());
    }


}
